import React, { useEffect, useState } from 'react';
import { Dialog, Form, Select } from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { find, get } from 'lodash';
import Text from '@/components/Text';
import TextSelect from '@/components/TextSelect';
import {
  boolYesNoOptions,
  showTypeOptions,
  variableLevelOptions,
  variableOptionalOptions,
} from '@/utils/constant';
import { getVariable, getVariableList } from '@/services/variableService';
import { UNSAFE_bugSetFieldsValue, isVoid, sleep, toTsOptions } from '@/utils/common';
import VarInput, { convertDetailToProps } from '../../Variables/components/VarInput';

const { FormItem } = Form;

// 变量模板
// scopeType: 0 主机 1 容器
function VariableModal({ id, visible, scopeType, handleClose, stencilList, variableLevel }) {
  const [form] = Form.useForm();
  const [options, setOptions] = useState([]);

  const showType = Form.useWatch('showType', form); // 展现形式
  const dependId = Form.useWatch('dependId', form); // 依赖
  const sameValue = Form.useWatch('sameValue', form); // 一致
  const showDetail = Form.useWatch('showDetail', form); // 展示参数

  const loadVariables = useMemoizedFn(async () => {
    const { data } = await getVariableList({ scopeType, variableLevel });
    setOptions(
      data.map((item) => ({
        label: item.key,
        value: item.id,
      })),
    );
  });

  const handleSubmit = ({ validateResult, fields }) => {
    if (validateResult === true) {
      handleClose(fields);
    }
  };

  const keySelect = useMemoizedFn(async (id) => {
    const { data: detail } = await getVariable(id);
    const originValues = detail.values || [];

    // 检查是否有不同的值
    let sameValue = true;
    originValues.forEach((item) => {
      if (item.value !== detail.defaultValue) {
        sameValue = false;
      }
    });

    // 重新根据stencilList同顺序排序，保证赋值的正确性
    const values = [];
    stencilList.forEach((stencil) => {
      values.push(find(originValues, { stencilId: stencil.id }));
    });

    const data = {
      ...detail,
      sameValue,
      values,
      showDetail: convertDetailToProps(detail.showDetail),
    };

    // 处理tdesign深层赋值bug，FormList:showDetail.options
    await UNSAFE_bugSetFieldsValue(form, data, 2);
    await sleep(100);
    form.setFields([{ name: ['showDetail', 'options'], value: get(data, 'showDetail.options') }]);
  });

  useEffect(() => {
    if (visible && id) {
      keySelect(id);
    }
  }, [id, visible, keySelect]);

  useEffect(() => {
    if (visible && !isVoid(scopeType)) {
      loadVariables();
    }
  }, [visible, loadVariables, scopeType]);

  useEffect(() => {
    if (!visible) {
      form.reset();
    }
  }, [visible, form]);

  return (
    <Dialog
      header='选择参数'
      visible={visible}
      onClose={() => handleClose()}
      confirmBtn={{ content: '确定' }}
      onConfirm={() => form.submit()}
      width={550}
      placement='center'
    >
      <Form form={form} onSubmit={handleSubmit}>
        <FormItem name='id' style={{ display: 'none' }} />
        <FormItem name='key' label='变量key' rules={[{ required: true }]}>
          <Select filterable options={toTsOptions(options)} onChange={keySelect} />
        </FormItem>
        <FormItem name='keyName' label='展示名称'>
          <Text />
        </FormItem>
        <FormItem name='variableLevel' label='变量范围'>
          <TextSelect options={variableLevelOptions} />
        </FormItem>
        <FormItem name='variableType' label='变量类型'>
          <TextSelect options={variableOptionalOptions} />
        </FormItem>
        <FormItem name='showType' label='展现形式'>
          <TextSelect options={showTypeOptions} />
        </FormItem>
        {/* 文本框 */}
        {showType === 1 && (
          <Block>
            <FormItem name={['showDetail', 'prepend']} label='前缀'>
              <Text empty='-' />
            </FormItem>
            <FormItem name={['showDetail', 'append']} label='后缀'>
              <Text empty='-' />
            </FormItem>
          </Block>
        )}
        {showType === 2 && (
          <Block>
            <Form.FormList name={['showDetail', 'options']}>
              {(fields) =>
                fields.map(({ key, name, ...restField }) => (
                  <FormItem key={key} {...restField} name={[name, 'value']} label='选项值'>
                    <Text />
                  </FormItem>
                ))
              }
            </Form.FormList>
          </Block>
        )}
        {showType === 3 && (
          <Block>
            <FormItem name={['showDetail', 'min']} label='最小值'>
              <Text empty='-' />
            </FormItem>
            <FormItem name={['showDetail', 'max']} label='最大值'>
              <Text empty='-' />
            </FormItem>
            <FormItem name={['showDetail', 'decimalPlaces']} label='小数位数'>
              <Text empty='-' />
            </FormItem>
            <FormItem name={['showDetail', 'suffix']} label='单位'>
              <Text empty='-' />
            </FormItem>
          </Block>
        )}
        <FormItem name='dependId' label='变量依赖'>
          <TextSelect options={options} empty='-' />
        </FormItem>
        {dependId && (
          <FormItem name='dependValue' label='值等于'>
            <Text />
          </FormItem>
        )}
        <FormItem name='groupName' label='所属分组'>
          <Text />
        </FormItem>
        <FormItem name='desc' label='变量备注'>
          <Text />
        </FormItem>

        <FormItem name='sameValue' label='默认值相同'>
          <TextSelect options={boolYesNoOptions} />
        </FormItem>
        {sameValue === true && (
          <FormItem name='defaultValue' label='默认值'>
            <VarInput type={showType} readOnly inputProps={showDetail} />
          </FormItem>
        )}
        {sameValue === false &&
          stencilList.map((item, index) => (
            <FormItem
              name={['values', index, 'value']}
              label={item.stencilName}
              labelAlign='top'
              key={item.id}
              style={{ paddingLeft: 10 }}
            >
              <VarInput type={showType} readOnly inputProps={showDetail} />
            </FormItem>
          ))}
      </Form>
    </Dialog>
  );
}

export default connect((state) => ({
  stencilList: state.variable.stencilList,
}))(VariableModal);

const Block = styled.div`
  border-radius: 12px;
  background: #f7f8fa;
  padding: 20px;
  margin-bottom: 20px;
`;
